import React from 'react';

import {
  getCoreRowModel,
  useReactTable,
  PaginationState,
  ColumnSort,
  RowSelectionState,
  TableOptions,
} from '@tanstack/react-table';

import { EntitlementsProject } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import {
  useTable,
  TableRow,
  TableCell,
  PaginationFooter,
  TableHeaderGroup,
  TableHeader,
} from '@adsk/alloy-react-table';

import { EmptyState } from './EmptyState';
import { usePagination } from './hooks/usePagination';
import { useSelection } from './hooks/useSelection';
import { useSorting } from './hooks/useSorting';
import { useTableConfiguration } from './hooks/useTableConfiguration';
import { LoadingState, LoadingStateProps } from './LoadingState';

export type ProjectsTableProps = {
  loading?: boolean;
  data: EntitlementsProject[];
  columns: TableOptions<EntitlementsProject>['columns'];
  recordCount: number;
  pagination: PaginationState;
  sorting: ColumnSort[];
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: ColumnSort[]) => void;
  renderLoadingState?: (props: LoadingStateProps) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  renderCell: (props: {
    children: React.ReactNode;
    row: EntitlementsProject;
  }) => React.ReactNode;
};

export const Table = ({
  data,
  loading,
  columns,
  pagination,
  sorting,
  recordCount,
  onRowSelectionChange,
  onPaginationChange,
  onSortingChange,
  renderLoadingState = (props) => <LoadingState {...props} />,
  renderEmptyState = () => <EmptyState />,
  renderCell = ({ children }) => children,
}: ProjectsTableProps) => {
  const { columnState, columnOptions, actionColumns, tableSize } =
    useTableConfiguration();
  const { paginationState, paginationOptions } = usePagination(
    recordCount,
    pagination,
    onPaginationChange,
  );
  const { sortingState, sortingOptions } = useSorting(sorting, onSortingChange);
  const { selectionState, selectionOptions } =
    useSelection(onRowSelectionChange);

  const table = useReactTable({
    data,
    columns: [...columns, ...actionColumns],
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    state: {
      ...selectionState,
      ...sortingState,
      ...paginationState,
      ...columnState,
    },
    ...paginationOptions,
    ...sortingOptions,
    ...selectionOptions,
    ...columnOptions,
  });

  const {
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable({ table, tableSize });

  const hasData = data.length > 0;

  return (
    <Flex
      flexDirection="column"
      width="100%"
      style={{
        flexFlow: 'column nowrap',
      }}
    >
      <Flex
        overflow="auto"
        flexDirection="column"
        width="100%"
        height="100%"
        padding="16px 0"
        style={{
          flexFlow: 'column nowrap',
        }}
      >
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderGroup
                key={headerGroup.id}
                {...getTableHeaderGroupProps(headerGroup)}
              >
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    role="columnheader"
                    {...getTableHeaderProps(header)}
                  />
                ))}
              </TableHeaderGroup>
            ))}
          </thead>
          <tbody>
            {loading ? (
              renderLoadingState({ table })
            ) : hasData ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  role="row"
                  data-testid={`row-${row.id}`}
                  {...getTableRowProps(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        backgroundColor: 'transparent',
                      }}
                      {...getTableCellProps(cell)}
                    >
                      {renderCell({
                        children: getTableCellProps(cell).children,
                        row: cell.row.original,
                      })}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <tr>
                <td>{renderEmptyState()}</td>
              </tr>
            )}
          </tbody>
        </table>
      </Flex>
      {!loading && hasData && (
        <PaginationFooter
          style={{
            marginTop: 16,
          }}
          canNextPage={table.getCanNextPage()}
          canPreviousPage={table.getCanPreviousPage()}
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          pageCount={table.getPageCount()}
          setPageIndex={table.setPageIndex}
          totalDataCount={recordCount}
        />
      )}
    </Flex>
  );
};
